import { motion } from "framer-motion";
import { FaGithub } from "react-icons/fa";
import { useState, useEffect } from "react";

const GitHubActivity = () => {
  const [contributions, setContributions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rankLoading, setRankLoading] = useState(true);

  useEffect(() => {
    // Fetch GitHub contribution data
    fetch('https://github-contributions-api.jogruber.de/v4/munji74')
      .then(res => res.json())
      .then(data => {
        setContributions(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching GitHub data:', err);
        setLoading(false);
      });

    // Simulate rank loading (will show "Top 5%" as placeholder)
    setTimeout(() => {
      setRankLoading(false);
    }, 500);
  }, []);

  const currentYear = new Date().getFullYear();
  const totalThisYear = contributions?.total?.[currentYear] || 0;

  return (
    <section id="github-activity" className="py-16 bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 transition-colors duration-300">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <FaGithub className="text-5xl text-gray-800 dark:text-gray-100" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-gray-100">
              GitHub Activity
            </h2>
          </div>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Consistent contribution history showing my dedication to coding and open-source development
          </p>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10"
        >
          {/* Total Contributions This Year */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-slate-700 transition-colors duration-300">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase">This Year</h3>
            </div>
            <p className="text-3xl font-bold text-gray-800 dark:text-gray-100">
              {loading ? '...' : totalThisYear.toLocaleString()}
              <span className="text-lg text-gray-500 dark:text-gray-400 ml-2">contributions</span>
            </p>
          </div>

          {/* Uganda Rank */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-slate-700 transition-colors duration-300">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center overflow-hidden rounded shadow-sm">
                <img
                  src="/uganda-flag.png"
                  alt="Uganda Flag"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase">Uganda Rank</h3>
            </div>
            <p className="text-3xl font-bold text-gray-800 dark:text-gray-100">
              {rankLoading ? '...' : 'Top 5%'}
              <span className="text-lg text-gray-500 dark:text-gray-400 ml-2">commits</span>
            </p>
          </div>

          {/* GitHub Profile */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-slate-700 transition-colors duration-300">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <FaGithub className="text-xl text-gray-700 dark:text-gray-300" />
              </div>
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase">Profile</h3>
            </div>
            <a
              href="https://github.com/munji74"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xl font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors inline-flex items-center gap-2"
            >
              @munji74
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </motion.div>

        {/* GitHub Calendar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100 dark:border-slate-700 transition-colors duration-300"
        >
          <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-6">
            Contribution Graph
          </h3>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100"></div>
              </div>
            ) : (
              <img
                src="https://ghchart.rshah.org/munji74"
                alt="GitHub Contribution Chart"
                className="w-full max-w-4xl mx-auto"
                style={{ imageRendering: 'auto' }}
              />
            )}
          </div>

          {/* Info */}
          <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p className="font-medium">
                {loading ? 'Loading...' : `${totalThisYear.toLocaleString()} contributions in ${currentYear}`}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Green squares represent days with commits • Darker = more activity
              </p>
            </div>

            <a
              href="https://github.com/munji74"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-slate-700 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-slate-600 transition-colors text-sm font-medium"
            >
              <FaGithub className="text-lg" />
              View Full Profile
            </a>
          </div>
        </motion.div>

        {/* Featured Repositories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {/* Repo Card 1 */}
          <a
            href="https://github.com/munji74/model_ipynb"
            target="_blank"
            rel="noopener noreferrer"
            className="group bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-slate-700 hover:shadow-xl hover:border-blue-300 dark:hover:border-blue-500 transition-all"
          >
            <div className="flex items-start justify-between mb-3">
              <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                Potato Disease Detection
              </h4>
              <svg className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              YOLOv5 model for detecting potato leaf diseases with 94.5% accuracy
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs rounded-full font-medium">Python</span>
              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs rounded-full font-medium">YOLOv5</span>
              <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs rounded-full font-medium">FastAPI</span>
            </div>
          </a>

          {/* Repo Card 2 */}
          <a
            href="https://github.com/munji74/portfolio_frontend"
            target="_blank"
            rel="noopener noreferrer"
            className="group bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-slate-700 hover:shadow-xl hover:border-blue-300 dark:hover:border-blue-500 transition-all"
          >
            <div className="flex items-start justify-between mb-3">
              <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                Portfolio Website
              </h4>
              <svg className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Modern React portfolio with Django backend and advanced animations
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 text-xs rounded-full font-medium">React</span>
              <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full font-medium">Django</span>
              <span className="px-2 py-1 bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400 text-xs rounded-full font-medium">Tailwind</span>
            </div>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default GitHubActivity;

